<template>
  <div>
    <div class="row">
      <div class="col-md-6 col-lg-4" v-for="channel in channels" :key="channel.code">
        <div
            class="hik-camera-tile"
            :class="{ 'hik-camera-tile--expanded': expandedChannel === channel.code }"
        >
          <div class="hik-camera-tile__preview" @click="handleTileClick(channel.code)">
            <video
                v-show="channelState[channel.code] === 'playing'"
                :ref="'video-' + channel.code"
                class="hik-camera-tile__video"
                :muted="isMuted(channel.code)"
                playsinline
            ></video>

            <button
                v-if="channelState[channel.code] === 'idle' || !channelState[channel.code]"
                type="button"
                class="hik-camera-tile__play"
            >
              <feather-icon icon="PlayIcon" size="24" />
            </button>
            <feather-icon
                v-else-if="channelState[channel.code] === 'loading'"
                icon="LoaderIcon"
                size="24"
                class="hik-camera-tile__spinner"
            />
            <feather-icon
                v-else-if="channelState[channel.code] === 'error'"
                icon="VideoOffIcon"
                size="24"
            />

            <button
                v-else-if="channelState[channel.code] === 'locked'"
                type="button"
                class="hik-camera-tile__locked"
                @click.stop="openUnlockModal(channel.code)"
            >
              <feather-icon icon="LockIcon" size="26" />
              <span>{{ $t('Unlock') }}</span>
            </button>

            <div v-if="channelState[channel.code] === 'playing'" class="hik-camera-tile__toolbar" @click.stop>
              <button type="button" class="hik-camera-tile__toolbar-btn" @click="toggleMute(channel.code)">
                <feather-icon :icon="isMuted(channel.code) ? 'VolumeXIcon' : 'Volume2Icon'" size="16" />
              </button>
              <button type="button" class="hik-camera-tile__toolbar-btn" @click="toggleExpand(channel.code)">
                <feather-icon :icon="expandedChannel === channel.code ? 'Minimize2Icon' : 'Maximize2Icon'" size="16" />
              </button>
            </div>
          </div>
          <div class="hik-camera-tile__footer">
            <span class="hik-camera-tile__label">{{ channel.label }}</span>
            <span class="hik-camera-tile__status">{{ channelStatusLabel(channel.code) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="expandedChannel" class="hik-camera-backdrop" @click="toggleExpand(expandedChannel)"></div>

    <b-modal
        :id="unlockModalId"
        hide-footer
        hide-header
        centered
        size="sm"
        content-class="hik-unlock-modal"
        @hidden="closeUnlockModal"
    >
      <form v-if="unlockModalChannel" class="hik-unlock-form" @submit.prevent="submitUnlock(unlockModalChannel)">
        <div class="hik-unlock-form__icon">
          <feather-icon icon="LockIcon" size="22" />
        </div>
        <h5 class="hik-unlock-form__title">{{ $t('This channel requires login to unlock') }}</h5>

        <div class="hik-unlock-form__field">
          <label>E-mail</label>
          <input
              type="email"
              v-model="unlockForms[unlockModalChannel].email"
              placeholder="E-mail"
              autocomplete="off"
              autofocus
              required
          />
        </div>

        <div class="hik-unlock-form__field">
          <label>{{ $t('Password') }}</label>
          <input
              type="password"
              v-model="unlockForms[unlockModalChannel].password"
              :placeholder="$t('Password')"
              autocomplete="off"
              required
          />
        </div>

        <p v-if="unlockForms[unlockModalChannel].error" class="hik-unlock-form__error">
          <feather-icon icon="AlertCircleIcon" size="14" /> {{ unlockForms[unlockModalChannel].error }}
        </p>

        <button type="submit" class="hik-unlock-form__submit" :disabled="unlockForms[unlockModalChannel].loading">
          {{ unlockForms[unlockModalChannel].loading ? $t('Unlocking...') : $t('Unlock') }}
        </button>
      </form>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import Hls from "hls.js";

export default {
  name: "HikCameraGrid",

  props: {
    deviceId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      channels: [
        { code: "1", label: this.$t("Canal 1") },
        { code: "2", label: this.$t("Canal 2") },
        { code: "3", label: this.$t("Canal 3") },
        { code: "4", label: this.$t("Canal 4") },
        { code: "5", label: this.$t("Canal 5") },
      ],
      // code -> 'idle' | 'loading' | 'playing' | 'error'
      channelState: {},
      // code -> boolean (undefined/true = mudo, false = com áudio)
      channelMuted: {},
      expandedChannel: null,
      unlockForms: {
        1: { email: "", password: "", loading: false, error: "" },
        2: { email: "", password: "", loading: false, error: "" },
        3: { email: "", password: "", loading: false, error: "" },
        4: { email: "", password: "", loading: false, error: "" },
        5: { email: "", password: "", loading: false, error: "" },
      },
      unlockModalChannel: null,
    };
  },

  computed: {
    unlockModalId() {
      return `hik-unlock-modal-${this._uid}`;
    },
  },

  watch: {
    deviceId() {
      this.destroyAllPlayers();
    },
  },

  created() {
    this.hlsInstances = {};
    this.retryTimers = {};
    this.retryCounts = {};
    this.unlockTokens = {};
    this.maxRetries = 3;
  },

  mounted() {
    window.addEventListener("keydown", this.handleKeydown);
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown);
    this.destroyAllPlayers();
  },

  methods: {
    channelStatusLabel(code) {
      const state = this.channelState[code];
      if (state === "playing") return this.$t("Em transmissão");
      if (state === "loading") return this.$t("Conectando...");
      if (state === "error") return this.$t("Sem sinal");
      if (state === "locked") return this.$t("This channel requires login to unlock");
      return this.$t("Clique para assistir");
    },

    handleTileClick(code) {
      const state = this.channelState[code];
      if (!state || state === "idle" || state === "error") {
        // clique manual sempre reseta o contador de retry automático
        this.retryCounts[code] = 0;
        this.startChannel(code);
      }
    },

    async startChannel(code) {
      this.setChannelState(code, "loading");

      let url;
      try {
        const params = { channel: code };
        if (this.unlockTokens[code]) params.unlock_token = this.unlockTokens[code];

        const response = await axios.get(
            `${this.$store.state.api}devices/${this.deviceId}/hik-camera-preview`,
            { params }
        );
        url = response.data && response.data.url;
        if (!url) throw new Error("URL de preview vazia");
      } catch (error) {
        if (error.response && error.response.status === 423) {
          // canal exige credenciais de um usuário específico antes de liberar o preview
          delete this.unlockTokens[code];
          this.setChannelState(code, "locked");
          return;
        }
        console.error("[HikCameraGrid] falha ao buscar a URL de preview", error);
        this.scheduleRetryOrFail(code);
        return;
      }

      try {
        this.attachPlayer(code, url);
      } catch (error) {
        console.error("[HikCameraGrid] falha ao iniciar o player", error);
        this.scheduleRetryOrFail(code);
      }
    },

    openUnlockModal(code) {
      this.unlockModalChannel = code;
      this.$bvModal.show(this.unlockModalId);
    },

    closeUnlockModal() {
      this.unlockModalChannel = null;
    },

    async submitUnlock(code) {
      const form = this.unlockForms[code];
      form.loading = true;
      form.error = "";

      try {
        const response = await axios.post(
            `${this.$store.state.api}devices/${this.deviceId}/hik-camera-preview/unlock`,
            { channel: Number(code), email: form.email, password: form.password }
        );
        this.unlockTokens[code] = response.data && response.data.token;
        form.password = "";
        this.retryCounts[code] = 0;
        this.$bvModal.hide(this.unlockModalId);
        await this.startChannel(code);
      } catch (error) {
        form.error = (error.response && error.response.data && error.response.data.error)
            || this.$t("Invalid credentials");
      } finally {
        form.loading = false;
      }
    },

    attachPlayer(code, url) {
      const ref = this.$refs["video-" + code];
      // Vue 2 sempre transforma ref em array quando o elemento está dentro de um v-for,
      // mesmo com um nome de ref único por iteração.
      const video = Array.isArray(ref) ? ref[0] : ref;
      if (!video) {
        console.error("[HikCameraGrid] elemento <video> não encontrado para o canal", code);
        return;
      }

      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = url;
        video.addEventListener("loadedmetadata", () => {
          this.retryCounts[code] = 0;
          this.setChannelState(code, "playing");
          video.play().catch(() => {});
        });
        video.addEventListener("error", () => this.scheduleRetryOrFail(code));
        return;
      }

      if (!Hls.isSupported()) {
        console.error("[HikCameraGrid] Hls.isSupported() retornou false");
        this.setChannelState(code, "error");
        return;
      }

      // liveSyncDurationCount menor + maxLiveSyncPlaybackRate = fica mais perto do "tempo real",
      // acelerando discretamente a reprodução (sem dar salto visível) quando o player atrasa,
      // em vez de manter a folga padrão de 3 segmentos do hls.js.
      const hls = new Hls({
        liveSyncDurationCount: 2,
        liveMaxLatencyDurationCount: 5,
        maxLiveSyncPlaybackRate: 1.3,
      });
      this.hlsInstances[code] = hls;
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this.retryCounts[code] = 0;
        this.setChannelState(code, "playing");
        video.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        console.error("[HikCameraGrid] Hls.js error", data);
        if (data.fatal) this.scheduleRetryOrFail(code);
      });
    },

    // O caminho 4G -> ISUP -> Streaming Server costuma falhar nas primeiras tentativas
    // e só estabilizar depois; em vez de exigir vários cliques manuais, tenta de novo
    // automaticamente (com backoff) antes de mostrar o estado de erro pro usuário.
    scheduleRetryOrFail(code) {
      this.destroyPlayer(code);

      const attempts = this.retryCounts[code] || 0;
      if (attempts >= this.maxRetries) {
        this.setChannelState(code, "error");
        return;
      }

      this.retryCounts[code] = attempts + 1;
      this.setChannelState(code, "loading");

      const delay = 1000 * 2 ** attempts; // 1s, 2s, 4s
      this.retryTimers[code] = setTimeout(() => {
        delete this.retryTimers[code];
        if (this.deviceId) this.startChannel(code);
      }, delay);
    },

    setChannelState(code, state) {
      this.channelState = { ...this.channelState, [code]: state };
    },

    isMuted(code) {
      return this.channelMuted[code] !== false;
    },

    toggleMute(code) {
      this.channelMuted = { ...this.channelMuted, [code]: !this.isMuted(code) };
    },

    toggleExpand(code) {
      this.expandedChannel = this.expandedChannel === code ? null : code;
    },

    handleKeydown(event) {
      if (event.key === "Escape" && this.expandedChannel) {
        this.expandedChannel = null;
      }
    },

    destroyPlayer(code) {
      if (this.hlsInstances[code]) {
        this.hlsInstances[code].destroy();
        delete this.hlsInstances[code];
      }
      if (this.retryTimers[code]) {
        clearTimeout(this.retryTimers[code]);
        delete this.retryTimers[code];
      }

      const ref = this.$refs["video-" + code];
      const video = Array.isArray(ref) ? ref[0] : ref;
      if (video) {
        video.pause();
        video.removeAttribute("src");
        video.load();
      }
    },

    destroyAllPlayers() {
      this.channels.forEach((channel) => this.destroyPlayer(channel.code));
      this.retryCounts = {};
      this.unlockTokens = {};
      this.channels.forEach((channel) => {
        const form = this.unlockForms[channel.code];
        form.email = "";
        form.password = "";
        form.error = "";
        form.loading = false;
      });
      this.channelState = {};
      this.expandedChannel = null;
      this.unlockModalChannel = null;
    },
  },
};
</script>

<style scoped>
.hik-camera-tile {
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  overflow: hidden;
  margin-bottom: 1.5rem;
  background: #fff;
}

.hik-camera-tile__preview {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 16 / 9;
  background: #1a1a1a;
  color: #b9b7c4;
  cursor: pointer;
}

.hik-camera-tile__video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hik-camera-tile__play {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.hik-camera-tile__spinner {
  animation: hik-camera-spin 1s linear infinite;
}

.hik-camera-tile__locked {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: transparent;
  color: #d7d5de;
  font-size: 0.8rem;
  font-weight: 600;
}

.hik-camera-tile__locked:hover {
  color: #fff;
}

.hik-camera-tile__toolbar {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.375rem;
  z-index: 2;
}

.hik-camera-tile__toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
}

.hik-camera-tile__toolbar-btn:hover {
  background: rgba(0, 0, 0, 0.75);
}

.hik-camera-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 1050;
}

.hik-camera-tile--expanded {
  position: fixed;
  top: 5vh;
  left: 5vw;
  width: 90vw;
  height: 90vh;
  z-index: 1051;
  display: flex;
  flex-direction: column;
}

.hik-camera-tile--expanded .hik-camera-tile__preview {
  aspect-ratio: unset;
  flex: 1 1 auto;
}

@keyframes hik-camera-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.hik-camera-tile__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
}

.hik-camera-tile__label {
  font-weight: 600;
}

.hik-camera-tile__status {
  font-size: 0.75rem;
  color: #6e6b7b;
}
</style>

<style>
.hik-unlock-modal {
  border-radius: 0.75rem;
  overflow: hidden;
}

.hik-unlock-modal .modal-body {
  padding: 2rem 1.75rem;
}
</style>

<style scoped>
.hik-unlock-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.hik-unlock-form__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: #eaf2ff;
  color: #007bff;
  margin-bottom: 0.75rem;
}

.hik-unlock-form__title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2b2b33;
  margin-bottom: 1.25rem;
}

.hik-unlock-form__field {
  width: 100%;
  text-align: left;
  margin-bottom: 1rem;
}

.hik-unlock-form__field label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6e6b7b;
  margin-bottom: 0.3rem;
}

.hik-unlock-form__field input {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border-radius: 0.4rem;
  border: 1px solid #d8d6de;
  font-size: 0.9rem;
}

.hik-unlock-form__field input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
}

.hik-unlock-form__error {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  width: 100%;
  font-size: 0.8rem;
  color: #dc3545;
  margin: -0.25rem 0 1rem;
}

.hik-unlock-form__submit {
  width: 100%;
  padding: 0.65rem;
  border-radius: 0.4rem;
  border: none;
  background: #007bff;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
}

.hik-unlock-form__submit:hover {
  background: #0062cc;
}

.hik-unlock-form__submit:disabled {
  opacity: 0.6;
}
</style>
